import React from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { reset } from 'redux-form'
import {
  DataTableWrapper,
  PageLayout,
  withApplicationNotificationsContext,
} from '@adc/ui-morpheus'
import { ROOT_CRUMB } from '../../constants/routing'
import { USERS } from '../../constants/tableTypes'
import { FORM_USER_MANAGEMENT } from '../../constants/formNames'
import {
  fetchTable,
  fetchTableAppend,
  fetchTableBatches,
  abortTableValues,
  resetTableValues,
  addFilter,
  clearFilters,
  removeFilter,
  changeSortBy,
} from '../../actions/users'
import { apiToUiDataRowsTranslation } from '../../translations/user'
import {
  columnSelector,
  getFilterClauses,
} from '../../reducers/users/columnDefinitions'
import { areRolesResiOnly, prepRoleSuggestions } from '../../util/roles'
import { prepPartyRoleSuggestions } from '../../util/partyRoles'

/**
 * Exported from statics, since the whole notification was static. If we want to have project-wide constants
 * for these, something like `constants/notifications.js`, where export exported member has the full notification
 * interface would be preferable.
 *
 * For now, keeping this as a scoped constant for exactly where it is used.
 */
const NOT_RESIDENTIAL_USER_ROLES_NOTIFICATION = {
  title: 'Sorry...',
  message: 'You may not edit users that have non-RESI roles.',
}

const UsersPage = props => {
  const {
    columns,
    filterClauses,
    addFilter,
    removeFilter,
    clearFilters,
    changeSortBy,
    fetchDataRows,
    fetchDataRowsAppend,
    isLoading,
    errorInfo,
    rowData,
    totalCount,
    suggestions,
    roleDefs,
    notifications,
    config,
    downloadData,
    downloadActions,
  } = props

  const checkEditPermission = rowIndex => {
    const roles = _.get(rowData[rowIndex], 'security.roles', [])

    if (!areRolesResiOnly(roles, roleDefs)) {
      notifications.toast.open({
        role: 'warn',
        message: NOT_RESIDENTIAL_USER_ROLES_NOTIFICATION,
      })
    }
  }

  const onRowAction = rowIndex => {
    checkEditPermission(rowIndex)
  }

  const tableProps = {
    columns,
    filterClauses,
    addFilter,
    removeFilter,
    clearFilters,
    changeSortBy,
    fetchDataRows,
    fetchDataRowsAppend,
    isLoading,
    errorInfo,
    rowData,
    totalCount,
    onRowAction,
    suggestions,
    resetForm: PropTypes.func.isRequired,
    downloadData,
    downloadActions,
  }

  return (
    <PageLayout
      title='User Management'
      crumbs={[ROOT_CRUMB, 'User Management']}
    >
      <DataTableWrapper {...config} {...tableProps} />
    </PageLayout>
  )
}

UsersPage.propTypes = {
  config: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  filterClauses: PropTypes.array.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  changeSortBy: PropTypes.func.isRequired,
  fetchDataRows: PropTypes.func.isRequired,
  fetchDataRowsAppend: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorInfo: PropTypes.object,
  rowData: PropTypes.array.isRequired,
  totalCount: PropTypes.number,
  suggestions: PropTypes.object,
  roleDefs: PropTypes.array.isRequired,
  notifications: PropTypes.object.isRequired,
  downloadData: PropTypes.object.isRequired,
  downloadActions: PropTypes.shape({
    fetchData: PropTypes.func.isRequired,
    abort: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  }),
}

const getTotalCount = state => state.total_count

const mapStateToProps = store => {
  const { content, isLoading, errorInfo } = store[USERS].data
  const downloadDataRaw = store[USERS].downloadData
  const downloadContent = downloadDataRaw.content
  const downloadData = {
    content: downloadContent || [],
    totalCount: downloadDataRaw.total_count,
    totalRetrieved: downloadDataRaw.total_elements,
    errorInfo: downloadDataRaw.errorInfo,
    isBatchSuccessful: downloadDataRaw.isBatchSuccessful,
  }
  const config = store[USERS].tableInfo.config
  return {
    config,
    isLoading: !!isLoading,
    columns: columnSelector(store[USERS].tableInfo),
    filterClauses: getFilterClauses(store[USERS].tableInfo),
    totalCount: getTotalCount(store[USERS].data),
    rowData: apiToUiDataRowsTranslation(
      content,
      store.roles.content,
      store.partyRoles.content
    ),
    errorInfo,
    suggestions: {
      suggestionData: {
        ...prepRoleSuggestions(store.roles.content),
        ...prepPartyRoleSuggestions(store.partyRoles.content),
      },
    },
    roleDefs: store.roles.content,
    downloadData,
  }
}

const mapDispatchToProps = dispatch => ({
  fetchDataRows: fetchOptions => dispatch(fetchTable(fetchOptions)),
  fetchDataRowsAppend: fetchOptions => dispatch(fetchTableAppend(fetchOptions)),
  addFilter: filterClause => dispatch(addFilter(filterClause)),
  removeFilter: filterClause => dispatch(removeFilter(filterClause)),
  clearFilters: _ => dispatch(clearFilters()),
  changeSortBy: (sortValues, columns) =>
    dispatch(changeSortBy(sortValues, columns)),
  resetForm: () => {
    dispatch(reset(FORM_USER_MANAGEMENT))
  },
  downloadActions: {
    fetchData: () => dispatch(fetchTableBatches()),
    abort: () => dispatch(abortTableValues(USERS, true)),
    reset: () => dispatch(resetTableValues(USERS, true)),
  },
})

export default withApplicationNotificationsContext(
  connect(mapStateToProps, mapDispatchToProps)(UsersPage)
)
